import { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import { currentPostAtom } from "../../../atom";

import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import styled, { css } from "styled-components";
import { Modal, Form, Button, Input, Switch, message, Dropdown, Select, notification, Alert } from "antd";
import UploadProgressModal from "../../upload/UploadProgressModal";
import { isUploadCancelled, useTusCancel } from "../../../hooks/useTusCancel";
import { EditOutlined, LoadingOutlined, SwapOutlined, SaveOutlined, CloseOutlined, PlusOutlined } from "@ant-design/icons";
import MainCategoryMenu from "./MainCategoryMenu";
import FileUploadInput from "../../upload/FileUploadInput";
import { useSlideQC } from "../../../hooks/useSlideQC";
import { SlideQCModal } from "../../upload/SlideQCModal";

import { getLoginUser } from "../../../api/user";
import { newGetCategory, getBoardListWithRootCategory } from "../../../api/categoryAPI";
import { createPost, getPostData, getPostList, changeCategory, postPost, reorderPosts } from "../../../api/postAPI";
import { createMediaGroup } from "../../../api/mediaGroupAPI";
import { ACCESS_TOKEN } from "../../../lib/cookie"
import { createSlideUpload } from "../../../lib/tusUpload";

const HEAVY_PROCESS_EXTENSIONS = ['zip', 'mrxs', 'ndpi'];

// 메인 직속 포스트 리스트를 가리키는 가상 서브 카테고리 ID
// (음수로 두어 실제 카테고리 ID와 충돌하지 않도록 함. 실제 API 호출 시에는 메인 ID를 사용)
const MAIN_VIRTUAL_SUB_ID = -1;

interface SortableItemProps {
  post: any;
  isOrderEditing: boolean;
  selectedPostId: string;
  isMultiSelected: boolean;
  onClick: (e: React.MouseEvent) => void;
  isAdmin: boolean;
  onContextMenu: (post: any) => void;
  processingStatus: Record<string, ProcessStatus>;
}

const SortableItem = ({ post, isOrderEditing, selectedPostId, isMultiSelected, onClick, isAdmin, onContextMenu, processingStatus }: SortableItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: post.id, disabled: !isOrderEditing });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || undefined,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 999 : "auto",
    touchAction: "none" as React.CSSProperties["touchAction"],
  };

  const status = processingStatus[post.id];
  const isProcessing = status && status.remaining > 0;
  const progressText = isProcessing ? `총 ${status.total}개 중 ${status.total - status.remaining}개 처리완료` : "";

  const renderContent = () => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
      <span>{post.title || post.name || "제목 없음"}</span>
      {isProcessing && (
        <div style={{ fontSize: '12px', color: '#faad14', display: 'flex', alignItems: 'center', gap: '5px' }}>
          <LoadingOutlined spin />
          <span>{progressText}</span>
        </div>
      )}
    </div>
  );

  // 1. 순서 변경 모드
  if (isOrderEditing) {
    return (
      <StPostItem
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        $isSelected={selectedPostId === String(post.id)}
        $isMultiSelected={false}
        $isOrderMode={true}
        $isProcessing={isProcessing}
      >
        {renderContent()}
      </StPostItem>
    );
  }

  // 2. 일반 모드 + 관리자 (컨텍스트 메뉴)
  if (isAdmin) {
    return (
      <Dropdown
        trigger={['contextMenu']}
        menu={{ items: [{ key: 'edit', label: '카테고리 편집', icon: <EditOutlined />, onClick: () => onContextMenu(post) }] }}
      >
        <StPostItem
          $isSelected={selectedPostId === String(post.id)}
          $isMultiSelected={isMultiSelected}
          onClick={onClick}
          $isOrderMode={false}
          $isProcessing={isProcessing}
        >
          {renderContent()}
        </StPostItem>
      </Dropdown>
    );
  }

  // 3. 일반 모드 + 유저
  return (
    <StPostItem
      $isSelected={selectedPostId === String(post.id)}
      $isMultiSelected={false}
      onClick={onClick}
      $isOrderMode={false}
      $isProcessing={isProcessing}
    >
      {renderContent()}
    </StPostItem>
  );
};

interface Props {
  rootCategoryId: number;
}

interface ProcessStatus {
  total: number;
  remaining: number;
}

const ConsultationItem = ({ rootCategoryId }: Props) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // 메인 카테고리 (root의 자식)
  const [categoryList, setCategoryList] = useState<any[]>([]);
  const [activeCategory, setActiveCategory] = useState<string>(searchParams.get("main") || "");

  // 서브 카테고리 (선택된 메인의 자식). 비어있으면 해당 메인은 서브 없음.
  const [subCategoryList, setSubCategoryList] = useState<any[]>([]);
  // 가상 "(메인)" 항목 + 실제 서브들로 구성된 메뉴 표시용 리스트
  const subMenuItems = useMemo(() => {
    if (!subCategoryList || subCategoryList.length === 0) return [];
    const mainCategory = categoryList.find(c => String(c.id) === activeCategory);
    return [
      { id: MAIN_VIRTUAL_SUB_ID, name: `(${mainCategory?.name ?? '메인'})`, listOrder: -1, __isVirtualMain: true },
      ...subCategoryList,
    ];
  }, [subCategoryList, categoryList, activeCategory]);

  // 현재 선택된 서브 카테고리. 빈 문자열이면 서브가 없는 메인을 본다는 뜻이거나 미선택.
  // 서브가 있는 메인일 때는 MAIN_VIRTUAL_SUB_ID(=-1) 또는 실제 서브 ID 중 하나가 들어감.
  const [activeSubCategory, setActiveSubCategory] = useState<string>(searchParams.get("sub") || "");

  const [postList, setPostList] = useState<any[]>([]);
  const [selectedPostId, setSelectedPostId] = useState<string>(searchParams.get("post") || "");
  const [currentPost, setCurrentPost] = useRecoilState(currentPostAtom);

  const [multiSelectedPostIds, setMultiSelectedPostIds] = useState<string[]>([]);
  const [editingPosts, setEditingPosts] = useState<any[]>([]);

  const [showSlideUploadModal, setShowSlideUploadModal] = useState<boolean>(false);
  const [showProgressModal, setShowProgressModal] = useState<boolean>(false);
  const [currentProgress, setCurrentProgress] = useState<number>(0);
  const [totalProgress, setTotalProgress] = useState<number>(0);
  const [uploadingFileName, setUploadingFileName] = useState<string>("");
  const [currentFileIndex, setCurrentFileIndex] = useState<number>(0);
  const [totalFiles, setTotalFiles] = useState<number>(0);
  const [slideNames, setSlideNames] = useState<Record<string, string>>({});
  const [fileList, setFileList] = useState<any[]>([]);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isOrderEditing, setIsOrderEditing] = useState(false);
  const [useGroupId, setUseGroupId] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<Record<string, ProcessStatus>>({});
  const { showQCModal, qcItems, qcDone, qcHasIssue, runQC, closeQCModal } = useSlideQC();
  const tusCancel = useTusCancel();

  /** 전송하지 못한 파일 수만큼 "처리 중" 카운트를 되돌린다 (안 하면 게시물이 계속 처리 중으로 잠김) */
  const releaseProcessingStatus = (postId: number, unsentCount: number) => {
    if (unsentCount <= 0) return;
    setProcessingStatus(prev => {
      const current = prev[postId];
      if (!current) return prev;
      return {
        ...prev,
        [postId]: {
          total: Math.max(0, current.total - unsentCount),
          remaining: Math.max(0, current.remaining - unsentCount),
        },
      };
    });
  };

  const [boardList, setBoardList] = useState<any[]>([]);
  const [editModalMains, setEditModalMains] = useState<any[]>([]);
  const [editModalSubs, setEditModalSubs] = useState<any[]>([]);
  const [editModalNoSub, setEditModalNoSub] = useState(false);
  const [form] = Form.useForm<any>();
  const currentBoard = useMemo(() => {
    return boardList.find(b => b.rootCategories?.[0]?.id === rootCategoryId);
  }, [boardList, rootCategoryId]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  /**
   * 현재 포스트 리스트가 어느 카테고리 ID 기준으로 조회되어야 하는지 계산
   * - 서브가 없는 메인: activeCategory 사용
   * - 서브가 있는 메인 + 가상 "(메인)" 선택: activeCategory 사용
   * - 서브가 있는 메인 + 실제 서브 선택: activeSubCategory 사용
   */
  const getEffectiveCategoryId = (): number | null => {
    if (subCategoryList.length === 0) {
      return activeCategory ? Number(activeCategory) : null;
    }
    if (!activeSubCategory) return null;
    const subId = Number(activeSubCategory);
    if (subId === MAIN_VIRTUAL_SUB_ID) {
      return activeCategory ? Number(activeCategory) : null;
    }
    return subId;
  };

  const updateParams = (params: { main?: string; sub?: string; post?: string; mainName?: string; subName?: string; clearSub?: boolean; clearPost?: boolean }) => {
    const newParams = new URLSearchParams(searchParams);
    if (params.main !== undefined) newParams.set("main", params.main);
    if (params.mainName !== undefined) newParams.set("mainName", params.mainName);

    if (params.clearSub) {
      newParams.delete("sub");
      newParams.delete("subName");
    } else {
      if (params.sub !== undefined) newParams.set("sub", params.sub);
      if (params.subName !== undefined) newParams.set("subName", params.subName);
    }

    if (params.clearPost) {
      newParams.delete("post");
    } else if (params.post !== undefined) {
      newParams.set("post", params.post);
    }

    setSearchParams(newParams, { replace: true });
  };

  const getPostListData = async () => {
    try {
      const targetId = getEffectiveCategoryId();
      if (!targetId) {
        setPostList([]);
        return;
      }
      const data = await getPostList(1, 9999, targetId, "id", "DESC");
      if (data?.length) data.sort((a: any, b: any) => a.listOrder - b.listOrder);
      setPostList(data || []);
    } catch (error) {
      console.error("Failed to fetch posts:", error);
    }
  };

  const handleContextMenu = (post: any) => {
    const postIdStr = String(post.id);
    if (multiSelectedPostIds.includes(postIdStr)) {
      const selectedPostsData = postList.filter(p => multiSelectedPostIds.includes(String(p.id)));
      openEditModal(selectedPostsData);
    } else {
      setMultiSelectedPostIds([]);
      openEditModal([post]);
    }
  };

  const openEditModal = (posts: any[]) => {
    setEditingPosts(posts);
    // 현재 게시판이 PATHOLOGY_PRACTICE라도 서브가 있을 수 있으니
    // 모달 내부에서는 별도로 mainId 선택 후 newGetCategory 결과로 판단
    setEditModalMains(categoryList);

    form.setFieldsValue({
      boardId: currentBoard?.board_id || undefined,
      mainId: Number(activeCategory),
      subId: undefined,
    });

    handleModalMainChange(Number(activeCategory));
    setIsEditModalOpen(true);
  };

  const handleModalBoardChange = async (boardId: number) => {
    const selectedBoard = boardList.find(b => b.board_id === boardId);
    const rootId = selectedBoard?.rootCategories?.[0]?.id;

    if (rootId) {
      const data = await newGetCategory(rootId);
      if (data?.length) data.sort((a: any, b: any) => a.listOrder - b.listOrder);
      setEditModalMains(data || []);
    } else {
      setEditModalMains([]);
    }

    setEditModalSubs([]);
    setEditModalNoSub(false);
    form.setFieldsValue({ mainId: undefined, subId: undefined });
  };

  const handleModalMainChange = async (mainId: number) => {
    const data = await newGetCategory(mainId);
    if (data?.length) data.sort((a: any, b: any) => a.listOrder - b.listOrder);
    const subs = data || [];
    setEditModalSubs(subs);
    setEditModalNoSub(subs.length === 0);
    form.setFieldsValue({ subId: undefined });
  };

  const onEditSubmit = async (values: any) => {
    try {
      if (editingPosts.length === 0) return;

      // 모달에서 서브가 비어있으면(=해당 메인에 서브 없음) 메인을, 있으면 서브를 대상으로
      const targetCategoryId = editModalNoSub ? values.mainId : values.subId;
      if (!targetCategoryId) {
        message.warning("이동할 카테고리를 선택해주세요.");
        return;
      }

      const data = editingPosts.map((post, index) => ({
        postId: post.id,
        categoryId: targetCategoryId,
        listOrder: index + 1
      }));

      await changeCategory(data);

      message.success(`${editingPosts.length}개의 포스트 카테고리가 변경되었습니다.`);
      setIsEditModalOpen(false);
      setMultiSelectedPostIds([]);
      setEditingPosts([]);
      getPostListData();
    } catch (error) {
      console.error("Failed to change category:", error);
      message.error("카테고리 변경에 실패했습니다.");
    }
  };

  const uploadFile = async (
    postId: number,
    file: any,
    fileIndex: number,
    totalFileCount: number,
    tracker: { successCount: number; notified: boolean },
    postTitle: string,
    hasHeavyFile: boolean,
    slideName: string = '',
  ) => {
    setUploadingFileName(file.name);
    setCurrentFileIndex(fileIndex + 1);

    return new Promise<void>((resolve, reject) => {
      let isResolved = false;

      const upload = createSlideUpload(file, {
        endpoint: process.env.REACT_APP_BACKEND_URL + "/api/slide/tus",
        headers: { Authorization: `Bearer ${localStorage.getItem(ACCESS_TOKEN)}` },
        chunkSize: 1024 * 1024 * 5,
        // 이어올리기를 쓰지 않으므로 이어올리기 정보도 저장하지 않는다
        // (남기면 AddSlide 에서 같은 파일을 올릴 때 이 업로드의 게시물로 이어올려질 수 있다)
        storeFingerprintForResuming: false,
        metadata: {
          postId: String(postId),
          filename: file.name || "",
          slidename: slideName,
          caption: "",
          gender: "",
          age: "",
          site: "",
          zindanName: "",
        },
        onError: reject,
        onProgress: function (bytesUploaded, bytesTotal) {
          const currentFilePercentage = ((bytesUploaded / bytesTotal) * 100).toFixed(2);
          setCurrentProgress(Number(currentFilePercentage));

          const completedFiles = fileIndex;
          const overallPercentage = ((completedFiles + (bytesUploaded / bytesTotal)) / totalFileCount * 100).toFixed(2);
          setTotalProgress(Number(overallPercentage));

          if (bytesUploaded === bytesTotal) {
            if (!isResolved) {
              isResolved = true;
              tusCancel.markSent(upload);
              setTimeout(() => {
                resolve();
              }, 500);
            }
          }
        },
        onSuccess: async () => {
          tracker.successCount++;
          console.log(`[Server] ${file.name} processing finished.`);

          setProcessingStatus(prev => {
            const currentStatus = prev[postId] || { total: 0, remaining: 0 };
            const newRemaining = currentStatus.remaining > 0 ? currentStatus.remaining - 1 : 0;

            return {
              ...prev,
              [postId]: { ...currentStatus, remaining: newRemaining }
            };
          });

          if (tracker.successCount === totalFileCount && !tracker.notified) {
            tracker.notified = true;
            console.log("All files processed.");

            await getPostListData();

            if (hasHeavyFile) {
              notification.success({
                key: `upload-complete-${postId}`,
                message: '처리 완료',
                description: `'${postTitle}'의 모든 파일 처리가 완료되어 조회가 가능합니다.`,
                duration: 5,
              });
            }
          }
        }
      }, { isCancelled: tusCancel.isCancelled });
      tusCancel.track(upload, reject);
      upload.start();
    });
  };

  const handleFileUpload = async (values: any) => {
    // 중지·오류 처리에서 쓰는 업로드 진행 정보
    let uploadPostId: number | null = null;
    let uploadTotalCount = 0;
    let sentCount = 0;
    try {
      if (isOrderEditing) return message.warning("순서 변경을 먼저 완료해주세요.");

      const targetId = getEffectiveCategoryId();
      if (!targetId) {
        message.error("업로드 대상 카테고리가 지정되지 않았습니다.");
        return;
      }

      const postId = await createPost(Number(targetId));
      if (!postId) throw new Error('Failed to create post');
      uploadPostId = postId;
      await postPost(postId, values.title, null);

      if (values.file) {
        const fileList = values.file.fileList;
        const postTitle = values.title || "새 포스트";
        const groupName = useGroupId ? (values.groupName || "").trim() : "";

        const hasHeavyFile = fileList.some((item: any) => {
          const ext = item.name.split('.').pop()?.toLowerCase();
          return ext && HEAVY_PROCESS_EXTENSIONS.includes(ext);
        });

        setProcessingStatus(prev => ({
          ...prev,
          [postId]: { total: fileList.length, remaining: fileList.length }
        }));

        setTotalFiles(fileList.length);
        setShowProgressModal(true);
        setCurrentProgress(0);
        setTotalProgress(0);
        setCurrentFileIndex(0);
        tusCancel.begin();
        uploadTotalCount = fileList.length;

        const tracker = { successCount: 0, notified: false };

        for (let i = 0; i < fileList.length; i++) {
          const file = fileList[i];
          const slideName = slideNames[file.uid] || '';
          tusCancel.throwIfCancelled();
          await uploadFile(postId, file.originFileObj, i, fileList.length, tracker, postTitle, hasHeavyFile, slideName);
          sentCount++;
        }

        // 새로 만든 게시글이라 업로드된 슬라이드 전부가 대상.
        // 그룹은 /api/media-groups 로 생성하면서 한 번에 배정한다 (TUS 메타데이터 groupId 대체).
        if (groupName) {
          try {
            const post = await getPostData(postId);
            const slideIds = (post?.slides ?? []).map((s: any) => s.slideId).filter(Boolean);
            if (slideIds.length > 0) {
              await createMediaGroup({
                postId,
                mediaType: "SLIDE",
                name: groupName,
                mediaIds: slideIds,
              });
            }
          } catch (e: any) {
            console.error("uploaded slide group assign error:", e);
            message.error(e?.message || "업로드는 완료됐지만 그룹 배정에 실패했습니다.");
          }
        }

        setShowProgressModal(false);
        setShowSlideUploadModal(false);
        form.resetFields();
        setUseGroupId(false);
        setSlideNames({});
        setFileList([]);
        runQC(postId, new Set(), fileList.map((item: any) => item.originFileObj?.name || item.name));

        if (hasHeavyFile) {
          notification.info({
            message: '대용량 파일 전송 완료',
            description: '파일 전송은 완료되었으나, 서버 처리에 시간이 소요될 수 있습니다.',
            duration: 5,
            placement: 'bottomRight',
          });
        } else {
          message.success("포스트 작성이 완료되었습니다.");
        }
      } else {
        setShowSlideUploadModal(false);
        form.resetFields();
        setUseGroupId(false);
        setSlideNames({});
        setFileList([]);
      }
    } catch (error) {
      setShowProgressModal(false);
      setShowSlideUploadModal(false);
      setSlideNames({});
      setFileList([]);
      if (uploadPostId != null) releaseProcessingStatus(uploadPostId, uploadTotalCount - sentCount);

      if (isUploadCancelled(error)) {
        // 중지: 게시물과 이미 전송된 파일은 그대로 둔다 (자동 삭제 X). 그룹 생성·QC 는 하지 않는다
        form.resetFields();
        setUseGroupId(false);
        notification.warning({
          message: "업로드를 중지했습니다",
          description:
            (sentCount > 0
              ? `전체 ${uploadTotalCount}개 중 전송이 끝난 ${sentCount}개 파일은 등록됩니다.`
              : "등록된 파일은 없습니다.") +
            " 게시물은 이미 만들어져 있으니 필요 없으면 삭제해주세요.",
          duration: 8,
          placement: "bottomRight",
        });
        return;
      }
      console.error("Upload error:", error);
      message.error("업로드 중 오류가 발생했습니다.");
    } finally {
      getPostListData();
    }
  };

  const handleMainCategoryChange = (category: string) => {
    const selectedCategory = categoryList.find(cat => String(cat.id) === category);
    setActiveCategory(category);
    setActiveSubCategory("");
    setSelectedPostId("");
    setMultiSelectedPostIds([]);
    setIsOrderEditing(false);
    setPostList([]);
    setSubCategoryList([]); // 새 메인의 서브 다시 fetch될 때까지 비움

    updateParams({
      main: category,
      mainName: selectedCategory?.name,
      clearSub: true,
      clearPost: true,
    });
  };

  const handleSubCategoryChange = (sub: string) => {
    if (isOrderEditing) {
      message.warning("순서 변경을 먼저 완료해주세요.");
      return;
    }
    setActiveSubCategory(sub);
    setSelectedPostId("");
    setMultiSelectedPostIds([]);
    setIsOrderEditing(false);

    const subItem = subMenuItems.find(s => String(s.id) === sub);
    const mainCategory = categoryList.find(cat => String(cat.id) === activeCategory);
    updateParams({
      main: activeCategory,
      sub,
      mainName: mainCategory?.name,
      subName: subItem?.name,
      clearPost: true,
    });
  };

  const handlePostClick = async (e: React.MouseEvent, postId: number) => {
    const status = processingStatus[postId];
    if (status && status.remaining > 0) {
      message.warning("서버에서 파일을 처리 중입니다. 잠시만 기다려주세요.");
      return;
    }

    const postIdStr = String(postId);

    if (isAdmin && (e.ctrlKey || e.metaKey)) {
      setMultiSelectedPostIds((prev) => {
        if (prev.includes(postIdStr)) {
          return prev.filter((id) => id !== postIdStr);
        } else {
          return [...prev, postIdStr];
        }
      });
      return;
    }
    setMultiSelectedPostIds([]);
    setSelectedPostId(postIdStr);

    const mainCategory = categoryList.find(cat => String(cat.id) === activeCategory);
    const subItem = subMenuItems.find(s => String(s.id) === activeSubCategory);
    updateParams({
      main: activeCategory,
      sub: activeSubCategory || undefined,
      post: postIdStr,
      mainName: mainCategory?.name,
      subName: subItem?.name,
    });
    setCurrentPost(await getPostData(postId));

    const params = new URLSearchParams();
    params.set('from', 'admin');

    const post = postList.find(p => p.id === postId);
    params.set('title', post?.title || post?.name || '제목 없음');

    const board = searchParams.get('board');
    const boardName = searchParams.get('boardName');

    if (board) params.set('board', board);
    if (boardName) params.set('boardName', boardName);
    if (activeCategory) params.set('main', activeCategory);
    if (mainCategory) params.set('mainName', mainCategory.name);
    if (activeSubCategory) params.set('sub', activeSubCategory);
    if (subItem) params.set('subName', subItem.name);

    navigate(`/post/${postId}?${params.toString()}`);
  };

  const handleAddPost = async () => {
    if (isOrderEditing) return message.warning("순서 변경을 먼저 완료해주세요.");
    const targetId = getEffectiveCategoryId();
    if (!targetId) {
      message.warning("포스트를 추가할 카테고리를 먼저 선택해주세요.");
      return;
    }
    setShowSlideUploadModal(true);
  };

  const getCategoryList = async () => {
    const data = await newGetCategory(rootCategoryId);
    if (data?.length) data.sort((a: any, b: any) => a.listOrder - b.listOrder);
    setCategoryList(data || []);
    const mainParam = searchParams.get("main");
    if (!mainParam && data && data.length > 0) {
      const firstMainId = String(data[0].id);
      setActiveCategory(firstMainId);
      updateParams({ main: firstMainId, mainName: data[0].name });
    }
  };

  const getSubCategoryList = async () => {
    if (!activeCategory) {
      setSubCategoryList([]);
      return;
    }
    const data = await newGetCategory(Number(activeCategory));
    if (data?.length) data.sort((a: any, b: any) => a.listOrder - b.listOrder);
    setSubCategoryList(data || []);

    if (data && data.length > 0) {
      // 서브 있음 - URL에 sub 있으면 유효성 체크 후 유지, 아니면 가상 메인 선택
      const subParam = searchParams.get("sub");
      const subParamNum = subParam ? Number(subParam) : null;
      const isValidSub =
        subParamNum !== null &&
        (subParamNum === MAIN_VIRTUAL_SUB_ID || data.some((s: any) => s.id === subParamNum));

      if (subParam && isValidSub) {
        setActiveSubCategory(subParam);
      } else {
        // 기본은 가상 "(메인)"
        const defaultSub = String(MAIN_VIRTUAL_SUB_ID);
        const mainCategory = categoryList.find(c => String(c.id) === activeCategory);
        setActiveSubCategory(defaultSub);
        updateParams({
          main: activeCategory,
          sub: defaultSub,
          mainName: mainCategory?.name,
          subName: `(${mainCategory?.name ?? '메인'})`,
          clearPost: true,
        });
      }
    } else {
      // 서브 없음 - sub 파라미터 제거
      setActiveSubCategory("");
      const newParams = new URLSearchParams(searchParams);
      if (newParams.has("sub")) {
        newParams.delete("sub");
        newParams.delete("subName");
        setSearchParams(newParams, { replace: true });
      }
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setPostList((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over?.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleSaveOrder = async () => {
    const reorderData = postList.map((post, index) => ({ postId: post.id, newOrder: index + 1 }));
    await reorderPosts(reorderData);
    message.success("순서가 저장되었습니다.");
    setIsOrderEditing(false);
  };

  const handleCancelOrder = () => {
    setIsOrderEditing(false);
    getPostListData();
  };

  useEffect(() => {
    const checkAdminPermission = async () => {
      try {
        const userData = await getLoginUser();
        setIsAdmin(userData?.role === 'ADMIN_MAIN');
      } catch (error) {
        setIsAdmin(false);
      }
    };
    checkAdminPermission();
    getCategoryList();

    getBoardListWithRootCategory().then(data => {
      if (data) {
        const filteredBoards = data.filter((b: any) => b.categoryType !== 'CONFERENCE');
        setBoardList(filteredBoards);
      }
    });
  }, []);

  // URL 변경 시 상태 동기화
  useEffect(() => {
    const mainParam = searchParams.get("main");
    const subParam = searchParams.get("sub");
    const postParam = searchParams.get("post");

    if (mainParam && mainParam !== activeCategory) {
      setActiveCategory(mainParam);
    }
    if (subParam !== null && subParam !== activeSubCategory) {
      setActiveSubCategory(subParam);
    }
    if (postParam && postParam !== selectedPostId) {
      setSelectedPostId(postParam);
    }
  }, [searchParams]);

  // 메인 카테고리 변경 시 서브 카테고리 fetch
  useEffect(() => {
    if (!activeCategory) return;
    if (activeCategory !== "0") {
      getSubCategoryList();
    }
  }, [activeCategory]);

  // 메인/서브가 확정되면 포스트 리스트 fetch
  useEffect(() => {
    if (!activeCategory || activeCategory === "0") return;
    // 서브가 있는 메인인데 아직 서브 선택이 안 된 상태면 대기
    if (subCategoryList.length > 0 && !activeSubCategory) return;
    getPostListData();
  }, [activeCategory, activeSubCategory, subCategoryList]);

  const hasSubs = subCategoryList.length > 0;

  return (
    <StContainer>
      <StMenuContainer>
        <MainCategoryMenu
          rootCategoryId={rootCategoryId}
          categoryType="PATHOLOGY_PRACTICE"
          categories={categoryList}
          activeCategory={activeCategory}
          onCategoryChange={handleMainCategoryChange}
          inheritPermissions={true}
          reloadCategory={async () => {
            // 메인 카테고리 우클릭으로 서브 카테고리를 추가했을 수도 있으니
            // 메인 + 현재 active 메인의 서브 리스트를 둘 다 갱신
            await getCategoryList();
            if (activeCategory && activeCategory !== "0") {
              await getSubCategoryList();
            }
          }}
        />

        {/* 서브 카테고리가 있을 때만 두 번째 컬럼 노출 */}
        {hasSubs && (
          <MainCategoryMenu
            rootCategoryId={Number(activeCategory)}
            categoryType="PATHOLOGY_PRACTICE"
            categories={subMenuItems}
            activeCategory={activeSubCategory}
            parentCategoryList={categoryList}
            onCategoryChange={handleSubCategoryChange}
            reloadCategory={getSubCategoryList}
            inheritPermissions={true}
          />
        )}
      </StMenuContainer>

      <div style={{ flex: 1 }}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={postList.map(p => p.id)}
            strategy={verticalListSortingStrategy}
          >
            <StPostList $isOrderEditing={isOrderEditing}>
              {postList.map((post) => (
                <SortableItem
                  key={post.id}
                  post={post}
                  isOrderEditing={isOrderEditing}
                  selectedPostId={selectedPostId}
                  isMultiSelected={multiSelectedPostIds.includes(String(post.id))}
                  onClick={(e) => handlePostClick(e, post.id)}
                  isAdmin={isAdmin}
                  onContextMenu={handleContextMenu}
                  processingStatus={processingStatus}
                />
              ))}

              {!isOrderEditing ? (
                <>
                  <StAddPostButton onClick={handleAddPost} $isSelected={false} $isOrderMode={false}>
                    <PlusOutlined /> Add Post
                  </StAddPostButton>

                  {isAdmin && postList.length > 1 && (
                    <StControlBox $isSelected={false} $isOrderMode={false}>
                      <Button icon={<SwapOutlined />} onClick={() => setIsOrderEditing(true)}>순서 변경</Button>
                    </StControlBox>
                  )}
                </>
              ) : (
                <StControlBox style={{ width: "100%", marginTop: 10 }} $isSelected={false} $isOrderMode={true}>
                  <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveOrder} style={{ marginRight: 8 }}>저장</Button>
                  <Button icon={<CloseOutlined />} onClick={handleCancelOrder} danger>취소</Button>
                </StControlBox>
              )}
            </StPostList>
          </SortableContext>
        </DndContext>
      </div>

      <Modal
        open={showSlideUploadModal}
        onCancel={() => {
          form.resetFields();
          setUseGroupId(false);
          setSlideNames({});
          setFileList([]);
          setShowSlideUploadModal(false);
        }}
        centered
        footer={null}
        width="40%"
      >
        <Form form={form} onFinish={handleFileUpload} layout="vertical">
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: "Please enter a title" }]}
          >
            <Input />
          </Form.Item>
          {/* 새 게시글이라 기존 그룹이 없다. 업로드 후 이 이름으로 그룹을 만들어 전부 배정한다. */}
          <Form.Item label="그룹 지정" style={{ marginBottom: useGroupId ? '8px' : '16px' }}>
            <Switch checked={useGroupId} onChange={(checked) => {
              setUseGroupId(checked);
              if (!checked) form.setFieldValue('groupName', undefined);
            }} />
          </Form.Item>
          {useGroupId && (
            <Form.Item
              name="groupName"
              label="그룹 이름"
              rules={[{ required: true, message: "그룹 이름을 입력해주세요." }]}
            >
              <Input placeholder="업로드한 슬라이드를 묶을 그룹 이름" maxLength={255} />
            </Form.Item>
          )}
          <Form.Item
            name="file"
            label="File"
            style={{ marginBottom: "0" }}
          >
            <FileUploadInput
              mode="slide"
              multiple={true}
              showSuccessMessage={false}
              onChange={(val) => setFileList(val?.fileList || [])}
            />
          </Form.Item>
          {fileList.length > 0 && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ marginBottom: '8px', fontWeight: 500 }}>슬라이드 이름 입력</div>
              {fileList.map((item: any) => (
                <div key={item.uid} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', gap: '8px' }}>
                  <span style={{ flex: '0 0 auto', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#595959', fontSize: '12px' }}>
                    {item.name}
                  </span>
                  <Input
                    placeholder="슬라이드 이름 (선택)"
                    value={slideNames[item.uid] || ''}
                    onChange={(e) => setSlideNames(prev => ({ ...prev, [item.uid]: e.target.value }))}
                    style={{ flex: 1 }}
                  />
                </div>
              ))}
            </div>
          )}
          <Form.Item style={{ marginTop: "20px" }}>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button onClick={() => {
                form.resetFields();
                setSlideNames({});
                setFileList([]);
                setShowSlideUploadModal(false);
              }} style={{ marginRight: "10px" }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Submit
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      <UploadProgressModal
        open={showProgressModal}
        fileName={uploadingFileName}
        fileIndex={currentFileIndex}
        totalFiles={totalFiles}
        currentPercent={currentProgress}
        totalPercent={totalProgress}
        onCancel={tusCancel.cancel}
        cancelling={tusCancel.cancelling}
      />

      <Modal
        title={`포스트 카테고리 변경 (${editingPosts.length}개)`}
        open={isEditModalOpen}
        onCancel={() => setIsEditModalOpen(false)}
        onOk={() => form.submit()}
        okText="변경하기"
        cancelText="취소"
        centered
      >
        <Form form={form} layout="vertical" onFinish={onEditSubmit} style={{ marginTop: 20 }}>
          {currentBoard?.name === "Consultation" && (
            <AdminInfoBox>
              <Alert
                message="게시판 이동 시 주의사항"
                description="Consultation 게시판에서 타 게시판으로 이동 시 게시글 내에 기입한 병리번호가 노출될 수 있으니 수정 후 변경해주세요."
                type="info"
              />
            </AdminInfoBox>
          )}
          <Form.Item
            name="boardId"
            label="게시판 선택"
            rules={[{ required: true, message: '게시판을 선택해주세요.' }]}
          >
            <Select
              placeholder="게시판 선택"
              options={boardList.map(b => ({ label: b.name, value: b.board_id }))}
              onChange={handleModalBoardChange}
            />
          </Form.Item>

          <Form.Item
            name="mainId"
            label="메인 카테고리 선택"
            rules={[{ required: true, message: '메인 카테고리를 선택해주세요.' }]}
          >
            <Select
              placeholder="메인 카테고리 선택"
              options={editModalMains.map(c => ({ label: c.name, value: c.id }))}
              onChange={(val) => handleModalMainChange(val)}
            />
          </Form.Item>

          {!editModalNoSub && (
            <Form.Item
              name="subId"
              label="서브 카테고리 선택"
              rules={[{ required: true, message: '서브 카테고리를 선택해주세요.' }]}
            >
              <Select
                placeholder="서브 카테고리 선택"
                options={editModalSubs.map(c => ({ label: c.name, value: c.id }))}
              />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <SlideQCModal
        open={showQCModal}
        items={qcItems}
        done={qcDone}
        hasIssue={qcHasIssue}
        onClose={closeQCModal}
      />
    </StContainer>
  );
};

const StContainer = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 20px;
  align-items: flex-start;
`;

const StMenuContainer = styled.div`
  display: flex;
  gap: 20px;
  flex-shrink: 0;
`;

const StPostList = styled.div<{ $isOrderEditing: boolean }>`
  display: flex;
  gap: 10px;
  width: 100%;
  
  ${props => props.$isOrderEditing ? css`
    flex-direction: column; 
    border: 2px dashed #1890ff;
    padding: 10px;
    border-radius: 8px;
    background-color: #f0f5ff;
  ` : css`
    flex-direction: column;
  `}
`;

const StPostItem = styled.div<{ $isSelected: boolean, $isMultiSelected?: boolean, $isOrderMode?: boolean, $isProcessing?: boolean }>`
  transition: background-color 0.2s, border-color 0.2s; 

  ${props => props.$isOrderMode ? css`
    width: 100%;
    padding: 12px 15px;
    
    border: 1px solid ${props.$isSelected ? '#4285f4' : '#d9d9d9'};
    border-radius: 4px;
    background-color: ${props.$isSelected ? '#e6f7ff' : '#fff'};
    
    display: flex;
    align-items: center;
    justify-content: flex-start;
    text-align: left;
    
    cursor: grab;
    &:active { cursor: grabbing; }

    &:hover {
      background-color: ${props.$isSelected ? '#bae7ff' : '#fafafa'};
      border-color: #40a9ff;
    }
  ` : css`
    width: 100%;
    padding: 12px 15px;
    
    border-bottom: 1px solid #f0f0f0;

    background-color: ${
      props.$isProcessing 
        ? '#fafafa' 
        : props.$isMultiSelected 
          ? '#d6e4ff'
          : props.$isSelected 
            ? '#e6f7ff'
            : 'transparent'
    };

    border-left: ${props.$isMultiSelected ? '4px solid #1890ff' : 'none'};
    
    opacity: ${props.$isProcessing ? 0.7 : 1};
    pointer-events: ${props.$isProcessing ? 'none' : 'auto'};
    cursor: ${props.$isProcessing ? 'not-allowed' : 'pointer'};
    
    border-radius: 4px;
    
    display: flex;
    align-items: center;
    justify-content: flex-start;
    text-align: left;
    
    font-size: 14px;
    color: #333;

    &:hover {
      background-color: ${
        props.$isProcessing 
          ? '#fafafa' 
          : props.$isMultiSelected 
            ? '#d6e4ff' 
            : (props.$isSelected ? '#d6efff' : '#fafafa')
      };
    }
    
    &:last-child {
      border-bottom: none;
    }
  `}
`;

const StAddPostButton = styled(StPostItem)`
  ${props => props.$isOrderMode ? css`
    border: 2px dashed #ccc;
    background-color: #fafafa;
    color: #666;
    font-weight: bold;
    justify-content: center;
    &:hover { background-color: #f0f0f0; border-color: #999; color: #333; }
  ` : css`
    border: 1px dashed #d9d9d9;
    border-radius: 4px;
    margin-top: 5px;
    justify-content: center;
    color: #888;
    background-color: #fff;
    &:hover { border-color: #40a9ff; color: #40a9ff; background-color: #fff; }
  `}
`;

const StControlBox = styled.div<{ $isSelected: boolean, $isOrderMode?: boolean }>`
  display: flex;
  align-items: center;
  margin-top: 10px;
  width: 100%;
  
  justify-content: ${props => props.$isOrderMode ? 'flex-end' : 'flex-end'};
`;

const AdminInfoBox = styled.div`
  margin-bottom: 24px;
  
  .ant-alert-info {
    background-color: #f0f7ff;
    border: 1px solid #adc6ff;
    border-radius: 8px;
  }
  
  .ant-alert-message {
    font-weight: bold;
    color: #1d39c4;
  }

  .ant-alert-description {
    color: #262626;
    font-size: 14px;
  }
`;

export default ConsultationItem;